import { AppService } from '@base/app.service';
import { EMPTY, ROLES } from '@utils';
import { CompanyService } from '@company/company.service';
import { UserService } from '@user/user.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { IUser, ISummaryCard } from '@base/interfaces';
import { Chart } from 'chart.js';

interface IGroupedUsers {
  administrators: { data: IUser[], info: ISummaryCard; };
  leaders: { data: IUser[], info: ISummaryCard; };
  analysts: { data: IUser[], info: ISummaryCard; };
  customers: { data: IUser[], info: ISummaryCard; };
}
@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.sass']
})
export class DashboardComponent implements OnInit, OnDestroy {
  ADMINISTRATORS = ROLES.ADMIN;
  LEADERS = ROLES.LEADER;
  ANALYSTS = ROLES.ANALYST;
  CUSTOMERS = ROLES.CUSTOMER;
  private subscriptions: Subscription[];
  users: IGroupedUsers;
  active_user: IUser;

  // para los graficos
  chart1: any;
  chart2: any;

  constructor(
    private userService: UserService,
    private companyService: CompanyService,
    private mainService: AppService,
  ) {
    this.active_user = EMPTY;
    this.subscriptions = [];
    this.users = {
      administrators: {
        data: [],
        info: {
          icon: 'assignment_ind',
          title: 'Administradores',
          subtitle: 'administradores registrados',
          link: 'administrators',
        }
      },
      leaders: {
        data: [],
        info: {
          icon: 'people',
          title: 'Líderes',
          subtitle: 'líderes registrados',
          link: 'leaders',
        }
      },
      analysts: {
        data: [],
        info: {
          icon: 'supervised_user_circle',
          title: 'Analistas',
          subtitle: 'analistas registrados',
          link: 'analysts',
        }
      },
      customers: {
        data: [],
        info: {
          icon: 'person',
          title: 'Clientes',
          subtitle: 'clientes registrados',
          link: 'customers',
        }
      },
    };

    this.userService.getUserStatusStats();
  }

  ngOnInit() {
    const usersSubscription: Subscription = this.userService.obsUserStats().subscribe(
      (data: object) => this.setInfo(data)
    );

    const loggedUserSubscription: Subscription = this.mainService.getCurrentUser().subscribe(
      (user: IUser) => {this.active_user = user; }
    );

    if (this.active_user.role['code'] !== ROLES.CUSTOMER) {
      const objTopChart: object = {max_companies: 3};
      const companiesSubscription: Subscription = this.companyService.getCompaniesDashChart(objTopChart)
        .subscribe(data => {
          this.chartConfig1(data);
          this.chartConfig2(data);
        });

      this.subscriptions = [...this.subscriptions, companiesSubscription];
    }

    this.subscriptions = [...this.subscriptions, usersSubscription, loggedUserSubscription];
  }

  ngOnDestroy() {
    this.subscriptions.map((subscription: Subscription) => subscription.unsubscribe());
  }

  private setInfo(data: object) {
    const auxStats = {
      administrators: {enabled: 0, disabled: 0},
      leaders: {enabled: 0, disabled: 0},
      analysts: {enabled: 0, disabled: 0},
      customers: {enabled: 0, disabled: 0},
    };
    const results = data['results'] as [];

    if (results !== null) {
      const roles = Array.from(Object.keys(auxStats));

      roles.map((role) => {
        const totals = results.filter((r) => r['code'] === role);
        const enabled = totals.find((status) => status['is_active'] === true);
        const disabled = totals.find((status) => status['is_active'] === false);
        auxStats[role]['enabled'] = (typeof enabled !== 'undefined') ? enabled['count'] : 0;
        auxStats[role]['disabled'] = (typeof disabled !== 'undefined') ? disabled['count'] : 0;

        this.users[role]['data'] = auxStats[role];
      });
    }
  }

  private chartConfig1(data) {
    let chartInfo = EMPTY;
    let companyLabels = [];
    let analystsData = [];
    let customersData = [];
    const dataSet: [] = [...data] as [];

    dataSet.map(item => {
      const totalAnalysts = parseInt(item['total_analysts'], 10);
      const totalCustomers = parseInt(item['total_customers'], 10);

      if (totalAnalysts > 0 || totalCustomers > 0)  {
        let companyName: string = item['name'];
        companyName = (companyName.length > 15) ? companyName.substr(0, 14) + '...' : companyName;
        companyLabels = [...companyLabels, companyName];
        analystsData = [...analystsData, totalAnalysts];
        customersData = [...customersData, totalCustomers];
      }
    });

    chartInfo = {
      type: 'horizontalBar',
      data: {
        labels: companyLabels,
        datasets: [
          {
            label: 'Analistas',
            data: analystsData,
            backgroundColor: 'rgba(187, 255, 132, 0.2)',
            borderColor: 'rgba(187,255,132,1)',
            borderWidth: 1
          },
          {
            label: 'Clientes',
            data: customersData,
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          },
        ]
      },
      beginAtZero: true,
      options: {
        responsive: true,
        scales: {
          xAxes: [{
            ticks: {
              beginAtZero: true,
              callback: function (value) { if (Number.isInteger(value)) { return value; } },
              stepSize: 1
            }
          }]
        }
      }
    };
    this.chart1 = new Chart('company-chart1', chartInfo);
  }

  private chartConfig2(data) {
    let chartInfo = EMPTY;
    let companyLabels = [];
    let groupsData = [];
    let modulesData = [];
    const dataSet: [] = [...data] as [];

    dataSet.map(item => {
      if (parseInt(item['total_groups'], 10) > 0 || parseInt(item['total_modules'], 10) > 0)  {
        let companyName: string = item['name'];
        companyName = (companyName.length > 15) ? companyName.substr(0, 14) + '...' : companyName;
        companyLabels = [...companyLabels, companyName];
        groupsData = [...groupsData, item['total_groups']];
        modulesData = [...modulesData, item['total_modules']];
      }
    });

    chartInfo = {
      type: 'horizontalBar',
      data: {
        labels: companyLabels,
        datasets: [
          {
            label: 'Grupos',
            data: groupsData,
            backgroundColor: 'rgba(183, 149, 11, 0.2)',
            borderColor: 'rgba(183,149,11,1)',
            borderWidth: 1
          },
          {
            label: 'Módulos',
            data: modulesData,
            backgroundColor: 'rgba(118, 68, 138, 0.2)',
            borderColor: 'rgba(118, 68, 138, 1)',
            borderWidth: 1
          },
        ]
      },
      beginAtZero: true,
      options: {
        responsive: true,
        scales: {
          xAxes: [{
            ticks: {
              beginAtZero: true,
              callback: function (value) { if (Number.isInteger(value)) { return value; } },
              stepSize: 1
            }
          }]
        }
      }
    };
    this.chart2 = new Chart('company-chart2', chartInfo);
  }
}
